<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { inject, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import type { Services } from "@services"
import { useSourceStore } from "@core/stores/source.store"

// ================================
// TYPES & INTERFACES
// ================================

type Props = {
    /** Identifiant du compteur (source) créé après signature du consentement */
    meterId: string
    /** Identifiant du consentement PCE pour lancer la déclaration GRDF */
    idConsent: string
    /**
     * Si true, le bouton « Terminer » n’est pas affiché (ex. parcours invitation :
     * la page parent gère la fin du flux).
     */
    hideTerminer?: boolean
}

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(defineProps<Props>(), {
    hideTerminer: false,
})
const emit = defineEmits<{ back: []; done: [] }>()

// ================================
// SERVICES & STORE
// ================================

const services = inject<Services>("services")
const sourceStore = useSourceStore()

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Déclaration GRDF",
            back: "Retour",
            terminer: "Terminer",
            loading: "Lancement de la déclaration GRDF en cours...",
            errorDeclare: "Impossible de lancer la déclaration GRDF.",
        },
    },
})

// ================================
// STATE
// ================================

const isInitializing = ref(true)
const initError = ref<string | null>(null)

// ================================
// INIT (déclaration + fetch source)
// ================================

/**
 * Lance la déclaration GRDF puis rafraîchit la source pour afficher le message / les actions (GrdfDdaMessageErrorContent).
 */
async function initializeDeclaration(): Promise<void> {
    if (!props.meterId || !props.idConsent) {
        initError.value = t("errorDeclare")
        isInitializing.value = false
        return
    }
    try {
        await services?.$declare(props.idConsent)
        await sourceStore.fetchSourceById({ _id_source: props.meterId })
    } catch (err) {
        initError.value = (err as Error)?.message ?? t("errorDeclare")
    } finally {
        isInitializing.value = false
    }
}

onMounted(() => {
    initializeDeclaration()
})

// ================================
// ACTIONS
// ================================

function onDone(): void {
    emit("done")
}
</script>

<template>
    <div class="meter-grdf-declaration flex flex-col gap-4">
        <header class="w-full text-center flex flex-col gap-4">
            <h2 class="text-lg font-semibold text-gray-700 text-center">
                {{ t("title") }}
            </h2>
        </header>

        <div
            v-if="isInitializing"
            class="flex flex-col items-center gap-4 py-6"
        >
            <BaseProgressSpinner style="width: 2rem; height: 2rem" />
            <p class="text-sm text-gray-600">{{ t("loading") }}</p>
        </div>

        <BaseMessage
            v-else-if="initError"
            severity="error"
            class="w-full"
            :closable="false"
        >
            {{ initError }}
        </BaseMessage>

        <div v-else class="flex flex-col gap-4">
            <MeterGrdfMessageError :meter-id="meterId" />
            <div
                v-if="!props.hideTerminer"
                class="flex gap-3 w-full justify-end"
            >
                <BaseButton
                    :label="t('terminer')"
                    class="min-w-[10rem]"
                    @click="onDone"
                />
            </div>
        </div>
    </div>
</template>
